import {Routes, Route, useLocation, Navigate, useParams} from 'react-router-dom'
import {useEffect, useMemo, useRef} from 'react'
import {useI18n} from '@/i18n'
import {startAction} from '@/observability/session'
import {BrandTheme, ThemeProvider} from '@/contexts/theme-context'
import {configuredDefaultBrandTheme, configuredDefaultTheme, isTenantMode} from '@/lib/runtime-config'
import {AuthProvider} from '@/contexts/auth-context'
import {ServerStatusProvider} from '@/contexts/server-status-context'
import {ServerUnavailableOverlay} from '@/components/server-unavailable-overlay'
import {ServiceProvider} from '@/services/context'
import {createRealServices} from '@/services/real'
import {HelpProvider} from '@/contexts/help-context'
import {HelpOverlay} from '@/components/help/HelpOverlay'
import {AnnouncerProvider, useAnnouncer} from '@/components/shared/Announcer'
import {PageTitleProvider, usePageTitleContext} from '@/contexts/page-title-context'
import {useAppConfig} from '@/hooks/use-app-config'
import {usePageTitle} from '@/hooks/use-page-title'
import {Header} from '@/components/layout/Header'
import {Footer} from '@/components/layout/Footer'
import {ImpersonationBanner} from '@/components/ImpersonationBanner'
import {Toaster} from '@/components/ui/toaster'
import {SkipToContentLink} from '@/components/layout/SkipToContentLink'
import {RouteChangeFocusManager} from '@/components/layout/RouteChangeFocusManager'
import {routes} from '@/lib/routes'
import {Dashboard} from '@/pages/Dashboard'
import {Admin} from '@/pages/Admin'
import {Schema} from '@/pages/Schema'
import {Roles} from '@/pages/Roles'
import {RoleDetail} from '@/pages/RoleDetail'
import {RoleAssignments} from '@/pages/RoleAssignments'
import {Connectors as ConnectorsLayout} from '@/pages/Connectors'
import {Connectors as OutboundConnectors} from '@/pages/OutboundConnectors'
import {OutboundConnector} from '@/features/outbound-connectors/OutboundConnectorDetail'
import {Types} from '@/pages/Types'
import {TypeDetail} from '@/pages/TypeDetail'
import {Profile} from '@/pages/Profile'
import {IdentityManagement} from '@/pages/IdentityManagement'
import {Parties} from '@/pages/Parties'
import {PartiesByType} from '@/pages/PartiesByType'
import {CreateParty} from '@/pages/CreateParty'
import {PartyDetail} from '@/pages/PartyDetail'
import {Identities} from '@/pages/Identities'
import {IdentitiesByType} from '@/pages/IdentitiesByType'
import {IdentityDetail} from '@/pages/IdentityDetail'
import {Accounts} from '@/pages/Accounts'
import {AccountDetail} from '@/pages/AccountDetail'
import {OnboardingWizard} from '@/pages/OnboardingWizard'
import {TargetSystemsResources} from '@/pages/TargetSystemsResources'
import {Systems} from '@/pages/Systems'
import {SystemsByType} from '@/pages/SystemsByType'
import {CreateTargetSystem} from '@/pages/CreateTargetSystem'
import {CreateResource} from '@/pages/CreateResource'
import {SystemDetail} from '@/pages/SystemDetail'
import {Resources} from '@/pages/Resources'
import {ResourcesByType} from '@/pages/ResourcesByType'
import {ResourceDetail} from '@/pages/ResourceDetail'
import {EnforcementObjects} from '@/pages/EnforcementObjects'
import {EnforcementObjectDetail} from '@/pages/EnforcementObjectDetail'
import {AccessGovernance} from '@/pages/AccessGovernance'
import {AccessProfiles} from '@/pages/AccessProfiles'
import {AccessProfileDetail} from '@/pages/AccessProfileDetail'
import {AccessProfileCreate} from '@/pages/AccessProfileCreate'
import {AccessProfileEdit} from '@/pages/AccessProfileEdit'
import {AutoAssignRuleRequests} from '@/pages/AutoAssignRuleRequests'
import {AutoAssignRuleRequestDetail} from '@/pages/AutoAssignRuleRequestDetail'
import {AutoAssignRuleRequestCreateWizard} from '@/pages/AutoAssignRuleRequestCreateWizard'
import {AutoAssignRuleRequestEdit} from '@/pages/AutoAssignRuleRequestEdit'
import {AutoAssignRules} from '@/pages/AutoAssignRules'
import {AutoAssignRuleDetail} from '@/pages/AutoAssignRuleDetail'
import {AutoAssignRuleExecutions} from '@/pages/AutoAssignRuleExecutions'
import {RestrictionRules} from '@/pages/RestrictionRules'
import {RestrictionRuleDetections} from '@/pages/RestrictionRuleDetections'
import {RestrictionRuleDetail} from '@/pages/RestrictionRuleDetail'
import {RestrictionRuleViolations} from '@/pages/RestrictionRuleViolations'
import {RestrictionRuleViolationsDashboard} from '@/pages/RestrictionRuleViolationsDashboard'
import {RestrictionRuleRequests} from '@/pages/RestrictionRuleRequests'
import {RestrictionRuleRequestCreateWizard} from '@/pages/RestrictionRuleRequestCreateWizard'
import {RestrictionRuleRequestDetail} from '@/pages/RestrictionRuleRequestDetail'
import {RestrictionRuleRequestEdit} from '@/pages/RestrictionRuleRequestEdit'
import {AccessRequests} from '@/pages/AccessRequests'
import {AccessRequestDetail} from '@/pages/AccessRequestDetail'
import {AccessRequestWizard} from '@/pages/AccessRequestWizard'
import {FunctionGroupDetail} from '@/pages/FunctionGroupDetail'
import {FunctionGroups} from '@/pages/FunctionGroups'
import {CreateFunctionGroup} from '@/pages/CreateFunctionGroup'
import {FunctionGroupRequests} from '@/pages/FunctionGroupRequests'
import {FunctionGroupRequestDetail} from '@/pages/FunctionGroupRequestDetail'
import {FunctionGroupRequestWizard} from '@/pages/FunctionGroupRequestWizard'
import {ApprovalPolicies} from '@/pages/ApprovalPolicies'
import {EscalationThresholds} from '@/pages/EscalationThresholds'
import {ApprovalPolicyDetail} from '@/pages/ApprovalPolicyDetail'
import {ApprovalPolicyCreate} from '@/pages/ApprovalPolicyCreate'
import {ApprovalPolicyEdit} from '@/pages/ApprovalPolicyEdit'
import {CertificationCampaigns} from '@/pages/CertificationCampaigns'
import {CertificationCampaignDetail} from '@/pages/CertificationCampaignDetail'
import {CertificationReviews} from '@/pages/CertificationReviews'
import {CertificationPolicies} from '@/pages/CertificationPolicies'
import {CertificationPolicyDetail} from '@/pages/CertificationPolicyDetail'
import {CertificationPolicyWizard} from '@/pages/CertificationPolicyWizard'
import {CertificationDashboard} from '@/pages/CertificationDashboard'
import {AssignmentDecisions} from '@/pages/AssignmentDecisions'
import {My} from '@/pages/My'
import {MyDashboard} from '@/pages/MyDashboard'
import {MyRequests} from '@/pages/MyRequests'
import {MyAccess} from '@/pages/MyAccess'
import {MyResponsibilities} from '@/pages/MyResponsibilities'
import {MyDelegations} from '@/pages/MyDelegations'
import {Delegations} from '@/pages/Delegations'
import {MyApprovals} from '@/pages/MyApprovals'
import {BackgroundJobs} from '@/pages/BackgroundJobs'
import {Analytics} from '@/pages/Analytics'
import {EffectiveAccessByIdentity} from '@/pages/analytics/EffectiveAccessByIdentity'
import {IdentityGrantsDetail} from '@/pages/analytics/IdentityGrantsDetail'
import {EffectiveAccessBySystem} from '@/pages/analytics/EffectiveAccessBySystem'
import {SystemGrantsDetail} from '@/pages/analytics/SystemGrantsDetail'
import {CompareGrants} from '@/pages/analytics/CompareGrants'
import {AccessAnalyticsDashboard} from '@/pages/analytics/AccessAnalyticsDashboard'
import {Provisioning} from '@/pages/Provisioning'
import {ProvisioningDashboard} from '@/pages/provisioning/ProvisioningDashboard'
import {ProvisioningTasks} from '@/pages/provisioning/ProvisioningTasks'
import {ProvisioningHistory} from '@/pages/provisioning/ProvisioningHistory'
import {ProvisioningTaskDetail} from '@/pages/provisioning/ProvisioningTaskDetail'
import {BackgroundJobsDashboard} from '@/pages/BackgroundJobsDashboard'
import {Jobs} from '@/pages/Jobs'
import {JobDetail} from '@/pages/JobDetail'
import {JobExecutions} from '@/pages/JobExecutions'
import {ExecutionDetail} from '@/pages/ExecutionDetail'
import {ClusterNodes} from '@/pages/ClusterNodes'
import {JobBuilderPage} from '@/pages/JobBuilderPage'
import {ScriptEditorPage} from '@/pages/ScriptEditorPage'
import {Reporting} from '@/pages/Reporting'
import {ReportCategoriesPage} from '@/pages/reporting/ReportCategoriesPage'
import {ReportDefinitionDetail} from '@/pages/reporting/ReportDefinitionDetail'
import {ReportDefinitionEdit} from '@/pages/reporting/ReportDefinitionEdit'
import {NotificationHistory} from '@/features/notifications/NotificationHistory'
import {NotificationDetail} from '@/features/notifications/NotificationDetail'
import {AdminNotificationLog} from '@/features/notifications/AdminNotificationLog'
import {AttributeDefinitions} from '@/pages/AttributeDefinitions'
import {RelationshipSchema} from '@/pages/RelationshipSchema'
import {SchemaPolicies} from '@/pages/SchemaPolicies'
import {SchemaPolicyEdit} from '@/pages/SchemaPolicyEdit'
import {SchemaViolations} from '@/pages/SchemaViolations'
import {ErrorLogs} from '@/pages/ErrorLogs'
import {ScriptExecuteLogPage} from '@/pages/admin/ScriptExecuteLogPage'
import {NotFound} from '@/pages/NotFound'
import {ErrorLogDetail} from '@/pages/ErrorLogDetail'
import {CachesAdmin} from '@/pages/CachesAdmin'
import {LlmPrompts} from '@/pages/LlmPrompts'
import {LlmPromptDetail} from '@/pages/LlmPromptDetail'
import {NotificationTemplates} from '@/pages/NotificationTemplates'
import {NotificationTemplateDetail} from '@/pages/NotificationTemplateDetail'
import {TestDataGenerator} from '@/pages/admin/TestDataGenerator'
import {UnifiedSearchBarDemo} from '@/components/demo/UnifiedSearchBarDemo'
import {FormLayoutListPage} from '@/pages/FormLayoutListPage'
import {FormLayoutDesignerPage} from '@/pages/FormLayoutDesignerPage'
import {InboundConnectors} from '@/pages/InboundConnectors'
import {InboundConnectorDetail} from '@/pages/InboundConnectorDetail'
import {InboundConnectorConfigLayout} from '@/features/inbound-connectors/InboundConnectorConfigLayout'
import {ConnectionSubPage} from '@/features/inbound-connectors/sub-pages/ConnectionSubPage'
import {ReconciliationSubPage} from '@/features/inbound-connectors/sub-pages/ReconciliationSubPage'
import {OperationsSubPage} from '@/features/inbound-connectors/sub-pages/OperationsSubPage'

const INBOUND_CONFIG_DEFAULT_SUBPAGE = 'connection'
const INBOUND_CONFIG_RECONCILIATION = 'reconciliation'
const INBOUND_CONFIG_OPERATIONS = 'operations'
const INBOUND_CONFIG_FALLBACK = '../connection'

import {RehireReview} from '@/pages/admin/RehireReview'
import {RehireReviewDetail} from '@/pages/admin/RehireReviewDetail'
import {LifecycleEvents} from '@/pages/admin/LifecycleEvents'
import {ServiceAccounts} from '@/pages/ServiceAccounts'
import {ServiceAccountDetail} from '@/pages/ServiceAccountDetail'
import {ValueLists} from '@/pages/ValueLists'
import {ValueListDetail} from '@/pages/ValueListDetail'
import {TypeValueDetail} from '@/pages/TypeValueDetail'
import {RequestInspector} from '@/pages/RequestInspector'
import {RequestInspectorDetail} from '@/pages/RequestInspectorDetail'
import {RequestDetail} from '@/pages/RequestDetail'
import {SectionContainer} from '@/components/shared/SectionContainer'


// WHY: legacy /access-governance/certification-reviews/:itemId/assignments
// callers (CertificationCampaignDetail row click, bookmarks, deep links from
// the access-governance section) need to land on the equivalent /my/reviews
// page WITH the itemId preserved. A bare <Navigate to=routes.my.reviews.list()>
// would drop the id and the user would arrive at the empty list page.
function CertificationReviewAssignmentsRedirect() {
    const {itemId} = useParams<{itemId: string}>()
    const search = useLocation().search
    if (!itemId) return <Navigate to={`${routes.my.reviews.list()}${search}`} replace/>
    return <Navigate to={`${routes.my.reviews.assignments(itemId)}${search}`} replace/>
}

// WHY: same rationale as CertificationReviewAssignmentsRedirect — the legacy
// access-governance list URL must preserve query-string filters (e.g.
// ?decision=IN_REVIEW deep links from CertificationDashboard widgets) when
// redirecting to /my/reviews. A bare <Navigate to=routes.my.reviews.list()>
// drops the search and the user lands on the unfiltered list.
function CertificationReviewListRedirect() {
    const search = useLocation().search
    return <Navigate to={`${routes.my.reviews.list()}${search}`} replace/>
}

function RouteActionTracker() {
    const location = useLocation()
    const endRef = useRef<(() => void) | null>(null)

    useEffect(() => {
        if (endRef.current) {
            endRef.current()
        }
        // eslint-disable-next-line i18next/no-literal-string
        endRef.current = startAction(`route-nav:${location.pathname}`)
        return () => {
            endRef.current?.()
            endRef.current = null
        }
    }, [location.pathname])

    return null
}

/* WHY: every page calls usePageTitle(...) which writes into PageTitleContext.
   The shell renders ONE <h1 className="sr-only"> here so SR users hear the
   page name on every navigation without each page repeating the H1. Issue
   794 — Class A structural fix.

   The component also owns its own announcement: useFocusOnRouteChange fires
   on `location.pathname` change which happens BEFORE the new page's
   PageLayout / usePageTitle effect runs, so the H1 still shows the stale
   previous title when the focus hook tries to announce. Watching the
   context title here and firing announce() only when it actually changes
   ensures SR users hear the NEW page name, never the previous one. Issue
   800 — F5/F6 follow-up. */
function PageH1() {
    const {title} = usePageTitleContext()
    const {announce} = useAnnouncer()
    const lastAnnouncedRef = useRef('')
    useEffect(() => {
        if (title && title !== lastAnnouncedRef.current) {
            announce(title)
            lastAnnouncedRef.current = title
        }
    }, [title, announce])
    return <h1 className="sr-only" data-testid="page-h1">{title}</h1>
}

/* WHY: i18n strings use `$t(appConfig.domainEntityTypes.X)` nested references
   that the AppConfig bridge resolves at runtime once `/api/i/config` returns.
   On a fresh page load the page renders BEFORE the bridge fires, so the H1
   contains raw keys like 'appConfig.domainEntityTypes.NOTIFICATIONS'. Gating
   the Routes on AppConfig isLoading guarantees every page first renders with
   resolved translations. Issue 794 — Class B fix. */
function AppConfigGate({children}: {children: React.ReactNode}) {
    const {isLoading} = useAppConfig()
    const i18n = useI18n()
    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-12" data-testid="app-config-loading">
                <span className="text-sm text-muted-foreground">{i18n.common.loading}</span>
            </div>
        )
    }
    return <>{children}</>
}

export function AppShell() {
    return (
        <ThemeProvider
            defaultTheme={configuredDefaultTheme() ?? 'light'}
            defaultBrandTheme={isTenantMode() ? BrandTheme.TENANT : (configuredDefaultBrandTheme() as BrandTheme | undefined) ?? BrandTheme.DEFAULT}
            storageKey="iam-ui-theme"
        >
            <HelpProvider>
                <AnnouncerProvider>
                <PageTitleProvider>
                <div className="min-h-screen bg-background text-foreground">
                    <SkipToContentLink/>
                    <ImpersonationBanner/>
                    <Header/>
                    <div>
                        <RouteChangeFocusManager/>
                        <RouteActionTracker/>
                        <PageH1/>
                        <AppConfigGate>
                        <Routes>
                            <Route path="/" element={<Dashboard/>}/>
                            <Route path={routes.profile()} element={<Profile/>}/>
                            <Route path={`/${routes.my.segment}`} element={<My/>}>
                                <Route index element={<MyDashboard/>}/>
                                <Route path={routes.my.access.segment} element={<MyAccess/>}/>
                                <Route path={routes.my.responsibilities.segment} element={<MyResponsibilities/>}/>
                                <Route path={routes.my.requests.segment} element={<MyRequests/>}/>
                                <Route path={`${routes.my.requests.segment}/new`} element={<AccessRequestWizard/>}/>
                                <Route path={`${routes.my.requests.segment}/:id`} element={<AccessRequestDetail/>}/>
                                <Route path={`${routes.my.functionGroupRequests.segment}/new`} element={<FunctionGroupRequestWizard/>}/>
                                <Route path={`${routes.my.functionGroupRequests.segment}/:id`} element={<FunctionGroupRequestDetail/>}/>
                                <Route path={routes.my.approvals.segment} element={<MyApprovals/>}/>
                                <Route path={routes.my.notifications.segment} element={<NotificationHistory/>}/>
                                <Route path={`${routes.my.notifications.segment}/:id`} element={<NotificationDetail/>}/>
                                <Route path={routes.my.delegations.segment} element={<MyDelegations/>}/>
                                <Route path={routes.my.rehires.segment} element={<RehireReview/>}/>
                                <Route path={`${routes.my.rehires.segment}/:id`} element={<RehireReviewDetail/>}/>
                                <Route path={routes.my.reviews.segment} element={<CertificationReviews/>}/>
                                <Route path={`${routes.my.reviews.segment}/:itemId/assignments`} element={<AssignmentDecisions/>}/>
                            </Route>
                            <Route path={`/${routes.admin.segment}`} element={<Admin/>}>
                                <Route path={routes.admin.roles.segment} element={<Roles/>}/>
                                <Route path={`${routes.admin.roles.segment}/:id`} element={<RoleDetail/>}/>
                                <Route path={routes.admin.roleAssignments.segment}
                                       element={<RoleAssignments/>}/>
                                <Route path={routes.admin.notificationLog.segment}
                                       element={<AdminNotificationLog/>}/>
                                <Route path={routes.admin.errorLogs.segment}
                                       element={<ErrorLogs/>}/>
                                <Route path={`${routes.admin.errorLogs.segment}/:id`}
                                       element={<ErrorLogDetail/>}/>
                                <Route path={routes.admin.scriptExecuteLog.segment}
                                       element={<ScriptExecuteLogPage/>}/>
                                <Route path={routes.admin.caches.segment}
                                       element={<CachesAdmin/>}/>
                                <Route path={routes.admin.llmPrompts.segment}
                                       element={<LlmPrompts/>}/>
                                <Route path={`${routes.admin.llmPrompts.segment}/:key`}
                                       element={<LlmPromptDetail/>}/>
                                <Route path={routes.admin.notificationTemplates.segment}
                                       element={<NotificationTemplates/>}/>
                                <Route path={`${routes.admin.notificationTemplates.segment}/:key`}
                                       element={<NotificationTemplateDetail/>}/>
                                <Route path={routes.admin.testData.segment}
                                       element={<TestDataGenerator/>}/>
                                <Route path={routes.admin.rehireReview.segment}
                                       element={<Navigate to={routes.my.rehires.list()} replace/>}/>
                                <Route path={`${routes.admin.rehireReview.segment}/:id`}
                                       element={<Navigate to={routes.my.rehires.list()} replace/>}/>
                                <Route path={routes.admin.lifecycleEvents.segment}
                                       element={<LifecycleEvents embedded/>}/>
                                <Route path={routes.admin.serviceAccounts.segment}
                                       element={<ServiceAccounts/>}/>
                                <Route path={`${routes.admin.serviceAccounts.segment}/:id`}
                                       element={<ServiceAccountDetail/>}/>
                                <Route path={routes.admin.requestInspector.segment}
                                       element={<RequestInspector/>}/>
                                <Route path={`${routes.admin.requestInspector.segment}/:sessionId`}
                                       element={<RequestInspectorDetail/>}/>
                                <Route path={`${routes.admin.requestInspector.segment}/:sessionId/request/:spanId`}
                                       element={<RequestDetail/>}/>
                            </Route>
                            <Route path={`/${routes.connectors.segment}`} element={<ConnectorsLayout/>}>
                                <Route path={routes.connectors.outbound.segment} element={<OutboundConnectors/>}/>
                                <Route path={`${routes.connectors.outbound.segment}/:id`} element={<OutboundConnector/>}/>
                                <Route path={routes.connectors.inbound.segment} element={<InboundConnectors/>}/>
                                <Route path={`${routes.connectors.inbound.segment}/:id`}
                                       element={<InboundConnectorDetail/>}>
                                    <Route path="sync-runs" element={null}/>
                                    <Route path={routes.connectors.inbound.configuration.segment}
                                           element={<InboundConnectorConfigLayout/>}>
                                        <Route index element={<Navigate to={INBOUND_CONFIG_DEFAULT_SUBPAGE} replace/>}/>
                                        <Route path={INBOUND_CONFIG_DEFAULT_SUBPAGE} element={<ConnectionSubPage/>}/>
                                        <Route path={INBOUND_CONFIG_RECONCILIATION} element={<ReconciliationSubPage/>}/>
                                        <Route path={INBOUND_CONFIG_OPERATIONS} element={<OperationsSubPage/>}/>
                                        <Route path="*" element={<Navigate to={INBOUND_CONFIG_FALLBACK} replace relative="path"/>}/>
                                    </Route>
                                </Route>
                            </Route>
                            <Route path={`/${routes.schema.segment}`} element={<Schema/>}>
                                <Route path={routes.schema.types.segment} element={<Types/>}/>
                                <Route path={`${routes.schema.types.segment}/:tableName`} element={<TypeDetail/>}/>
                                <Route path={`${routes.schema.types.segment}/:tableName/:code`} element={<TypeValueDetail/>}/>
                                <Route path={routes.schema.attributeDefinitions.segment}
                                       element={<AttributeDefinitions/>}/>
                                <Route path={routes.schema.formLayouts.segment}
                                       element={<FormLayoutListPage/>}/>
                                <Route path={`${routes.schema.formLayouts.segment}/:useCaseKey`}
                                       element={<FormLayoutDesignerPage/>}/>
                                <Route path={routes.schema.relationshipSchema.segment}
                                       element={<RelationshipSchema/>}/>
                                <Route path={routes.schema.valueLists.segment}
                                       element={<ValueLists/>}/>
                                <Route path={`${routes.schema.valueLists.segment}/:id`}
                                       element={<ValueListDetail/>}/>
                                <Route path={routes.schema.policies.segment}
                                       element={<SchemaPolicies/>}/>
                                <Route path={`${routes.schema.policies.segment}/new`}
                                       element={<SchemaPolicyEdit/>}/>
                                <Route path={`${routes.schema.policies.segment}/:id/edit`}
                                       element={<SchemaPolicyEdit/>}/>
                                <Route path={routes.schema.violations.segment}
                                       element={<SchemaViolations/>}/>
                            </Route>
                            <Route path={`/${routes.identityManagement.segment}`} element={<IdentityManagement/>}>
                                <Route path={routes.identityManagement.parties.segment} element={<Parties/>}/>
                                <Route path={`${routes.identityManagement.parties.segment}/by-type/:typeCode/new`}
                                       element={<CreateParty/>}/>
                                <Route path={`${routes.identityManagement.parties.segment}/by-type/:typeCode`}
                                       element={<PartiesByType/>}/>
                                <Route path={`${routes.identityManagement.parties.segment}/:id`}
                                       element={<PartyDetail/>}/>
                                <Route path={routes.identityManagement.identities.segment}
                                       element={<Identities/>}/>
                                <Route path={`${routes.identityManagement.identities.segment}/by-type/:typeCode`}
                                       element={<IdentitiesByType/>}/>
                                <Route path={`${routes.identityManagement.identities.segment}/:id`}
                                       element={<IdentityDetail/>}/>
                                <Route path={routes.identityManagement.accounts.segment}
                                       element={<Accounts/>}/>
                                <Route path={`${routes.identityManagement.accounts.segment}/:id`}
                                       element={<AccountDetail/>}/>
                                <Route path={routes.identityManagement.onboarding.segment}
                                       element={<OnboardingWizard/>}/>
                            </Route>
                            <Route path={`/${routes.systems.segment}`} element={<TargetSystemsResources/>}>
                                <Route path={routes.systems.listSegment} element={<Systems/>}/>
                                <Route path={`${routes.systems.listSegment}/by-type/:typeCode/new`} element={<CreateTargetSystem/>}/>
                                <Route path={`${routes.systems.listSegment}/by-type/:typeCode`} element={<SystemsByType/>}/>
                                <Route path={`${routes.systems.listSegment}/:id`} element={<SystemDetail/>}/>
                                <Route path={routes.systems.resources.segment} element={<Resources/>}/>
                                <Route path={`${routes.systems.resources.segment}/by-type/:typeCode/new`} element={<CreateResource/>}/>
                                <Route path={`${routes.systems.resources.segment}/by-type/:typeCode`} element={<ResourcesByType/>}/>
                                <Route path={`${routes.systems.resources.segment}/:id`} element={<ResourceDetail/>}/>
                                <Route path={routes.systems.enforcementObjects.segment}
                                       element={<EnforcementObjects/>}/>
                                <Route path={`${routes.systems.enforcementObjects.segment}/:id`}
                                       element={<EnforcementObjectDetail/>}/>
                            </Route>
                            <Route path={`/${routes.accessGovernance.segment}`} element={<AccessGovernance/>}>
                                <Route path={routes.accessGovernance.profiles.segment}
                                       element={<AccessProfiles/>}/>
                                <Route path={`${routes.accessGovernance.profiles.segment}/new`}
                                       element={<AccessProfileCreate/>}/>
                                <Route path={`${routes.accessGovernance.profiles.segment}/:id`}
                                       element={<AccessProfileDetail/>}/>
                                <Route path={`${routes.accessGovernance.profiles.segment}/:id/edit`}
                                       element={<AccessProfileEdit/>}/>
                                <Route path={routes.accessGovernance.rules.segment}
                                       element={<AutoAssignRules/>}/>
                                <Route path={`${routes.accessGovernance.rules.segment}/executions`}
                                       element={<AutoAssignRuleExecutions/>}/>
                                <Route path={`${routes.accessGovernance.rules.segment}/new`}
                                       element={<Navigate to={routes.accessGovernance.autoAssignRuleRequests.create()} replace/>}/>
                                <Route path={`${routes.accessGovernance.rules.segment}/:id`}
                                       element={<AutoAssignRuleDetail/>}/>
                                <Route path={routes.accessGovernance.autoAssignRuleRequests.segment}
                                       element={<AutoAssignRuleRequests/>}/>
                                <Route path={`${routes.accessGovernance.autoAssignRuleRequests.segment}/new`}
                                       element={<AutoAssignRuleRequestCreateWizard/>}/>
                                <Route path={`${routes.accessGovernance.autoAssignRuleRequests.segment}/:id`}
                                       element={<AutoAssignRuleRequestDetail/>}/>
                                <Route path={`${routes.accessGovernance.autoAssignRuleRequests.segment}/:id/edit`}
                                       element={<AutoAssignRuleRequestEdit/>}/>
                                <Route path={routes.accessGovernance.restrictionRules.segment}
                                       element={<RestrictionRules/>}/>
                                <Route path={`${routes.accessGovernance.restrictionRules.segment}/detections`}
                                       element={<RestrictionRuleDetections/>}/>
                                <Route path={`${routes.accessGovernance.restrictionRules.segment}/new`}
                                       element={<RestrictionRuleRequestCreateWizard/>}/>
                                <Route path={`${routes.accessGovernance.restrictionRules.segment}/:id`}
                                       element={<RestrictionRuleDetail/>}/>
                                <Route path={`${routes.accessGovernance.restrictionRules.segment}/:id/violations`}
                                       element={<RestrictionRuleViolations/>}/>
                                <Route path={routes.accessGovernance.restrictionRuleRequests.segment}
                                       element={<RestrictionRuleRequests/>}/>
                                <Route path={`${routes.accessGovernance.restrictionRuleRequests.segment}/new`}
                                       element={<RestrictionRuleRequestCreateWizard/>}/>
                                <Route path={`${routes.accessGovernance.restrictionRuleRequests.segment}/:id`}
                                       element={<RestrictionRuleRequestDetail/>}/>
                                <Route path={`${routes.accessGovernance.restrictionRuleRequests.segment}/:id/edit`}
                                       element={<RestrictionRuleRequestEdit/>}/>
                                <Route path={routes.accessGovernance.violationsDashboard.segment}
                                       element={<RestrictionRuleViolationsDashboard/>}/>
                                <Route path={routes.accessGovernance.accessRequests.segment}
                                       element={<AccessRequests/>}/>
                                <Route path={`${routes.accessGovernance.accessRequests.segment}/new`}
                                       element={<AccessRequestWizard/>}/>
                                <Route path={`${routes.accessGovernance.accessRequests.segment}/:id`}
                                       element={<AccessRequestDetail/>}/>
                                <Route path={routes.accessGovernance.functionGroups.segment}
                                       element={<FunctionGroups/>}/>
                                <Route path={`${routes.accessGovernance.functionGroups.segment}/new`}
                                       element={<CreateFunctionGroup/>}/>
                                <Route path={`${routes.accessGovernance.functionGroups.segment}/:id`}
                                       element={<FunctionGroupDetail/>}/>
                                <Route path={routes.accessGovernance.functionGroupRequests.segment}
                                       element={<FunctionGroupRequests/>}/>
                                <Route path={`${routes.accessGovernance.functionGroupRequests.segment}/new`}
                                       element={<FunctionGroupRequestWizard/>}/>
                                <Route path={`${routes.accessGovernance.functionGroupRequests.segment}/:id`}
                                       element={<FunctionGroupRequestDetail/>}/>
                                <Route path={routes.accessGovernance.approvalPolicies.segment}
                                       element={<ApprovalPolicies/>}/>
                                <Route path={`${routes.accessGovernance.approvalPolicies.segment}/new`}
                                       element={<ApprovalPolicyCreate/>}/>
                                <Route path={`${routes.accessGovernance.approvalPolicies.segment}/:id`}
                                       element={<ApprovalPolicyDetail/>}/>
                                <Route path={`${routes.accessGovernance.approvalPolicies.segment}/:id/edit`}
                                       element={<ApprovalPolicyEdit/>}/>
                                <Route path={routes.accessGovernance.escalationThresholds.segment}
                                       element={<EscalationThresholds/>}/>
                                <Route path={routes.accessGovernance.certificationCampaigns.segment}
                                       element={<CertificationCampaigns/>}/>
                                <Route path={`${routes.accessGovernance.certificationCampaigns.segment}/:id`}
                                       element={<CertificationCampaignDetail/>}/>
                                <Route path={routes.accessGovernance.certificationReviews.segment}
                                       element={<CertificationReviewListRedirect/>}/>
                                <Route path={`${routes.accessGovernance.certificationReviews.segment}/:itemId/assignments`}
                                       element={<CertificationReviewAssignmentsRedirect/>}/>
                                <Route path={routes.accessGovernance.certificationPolicies.segment}
                                       element={<CertificationPolicies/>}/>
                                <Route path={`${routes.accessGovernance.certificationPolicies.segment}/new`}
                                       element={<CertificationPolicyWizard mode="create"/>}/>
                                <Route path={`${routes.accessGovernance.certificationPolicies.segment}/:id/edit`}
                                       element={<CertificationPolicyWizard mode="edit"/>}/>
                                <Route path={`${routes.accessGovernance.certificationPolicies.segment}/:id`}
                                       element={<CertificationPolicyDetail/>}/>
                                <Route path={routes.accessGovernance.certificationDashboard.segment}
                                       element={<CertificationDashboard/>}/>
                                <Route path={routes.accessGovernance.delegations.segment}
                                       element={<Delegations/>}/>
                            </Route>
                            <Route path={`/${routes.backgroundJobs.segment}`} element={<BackgroundJobs/>}>
                                <Route index element={<Navigate to={routes.backgroundJobs.dashboard.path()} replace/>}/>
                                <Route path={routes.backgroundJobs.dashboard.segment}
                                       element={<BackgroundJobsDashboard/>}/>
                                <Route path={routes.backgroundJobs.jobs.segment} element={<Jobs/>}/>
                                <Route path={`${routes.backgroundJobs.jobs.segment}/:id`} element={<JobDetail/>}/>
                                <Route path={routes.backgroundJobs.executions.segment}
                                       element={<JobExecutions/>}/>
                                <Route path={`${routes.backgroundJobs.executions.segment}/:id`}
                                       element={<ExecutionDetail/>}/>
                                <Route path={routes.backgroundJobs.cluster.segment} element={<ClusterNodes/>}/>
                                <Route path={`${routes.backgroundJobs.builder.segment}/:id`} element={<JobBuilderPage/>}/>
                                <Route path={`${routes.backgroundJobs.builder.segment}/:id/script/:taskId`} element={<ScriptEditorPage/>}/>
                            </Route>
                            <Route path={`/${routes.effectiveAccess.segment}`} element={<Analytics/>}>
                                <Route index element={<Navigate to={routes.effectiveAccess.dashboard.path()} replace/>}/>
                                <Route path={routes.effectiveAccess.dashboard.segment}
                                       element={<AccessAnalyticsDashboard/>}/>
                                <Route path={routes.effectiveAccess.identities.segment}
                                       element={<EffectiveAccessByIdentity/>}/>
                                <Route path={`${routes.effectiveAccess.identities.segment}/:id`}
                                       element={<IdentityGrantsDetail/>}/>
                                <Route path={routes.effectiveAccess.systems.segment}
                                       element={<EffectiveAccessBySystem/>}/>
                                <Route path={`${routes.effectiveAccess.systems.segment}/:id`}
                                       element={<SystemGrantsDetail/>}/>
                                <Route path={routes.effectiveAccess.compare.segment} element={<CompareGrants/>}/>
                            </Route>
                            <Route path={`/${routes.provisioning.segment}`} element={<Provisioning/>}>
                                <Route path={routes.provisioning.dashboard.segment} element={<ProvisioningDashboard/>}/>
                                <Route path={routes.provisioning.tasks.segment} element={<ProvisioningTasks/>}/>
                                <Route path={`${routes.provisioning.tasks.segment}/:id`} element={<ProvisioningTaskDetail/>}/>
                                <Route path={routes.provisioning.history.segment} element={<ProvisioningHistory/>}/>
                            </Route>
                            <Route path={`/${routes.reporting.segment}`} element={<Reporting/>}>
                                <Route path={routes.reporting.categories.segment} element={<ReportCategoriesPage/>}/>
                                <Route path={`${routes.reporting.reports.segment}/new`} element={<ReportDefinitionEdit/>}/>
                                <Route path={`${routes.reporting.reports.segment}/:id`} element={<ReportDefinitionDetail/>}/>
                                <Route path={`${routes.reporting.reports.segment}/:id/edit`} element={<ReportDefinitionEdit/>}/>
                            </Route>
                            <Route path={`/${routes.auditLog.segment}`} element={<PlaceholderPage title="Audit Log"/>}/>
                            {/* Dev-only demo harness for UnifiedSearchBar. Not linked from navigation. */}
                            <Route path="/demo/unified-search-bar" element={<UnifiedSearchBarDemo/>}/>
                            <Route path="*" element={<NotFound/>}/>
                        </Routes>
                        </AppConfigGate>
                    </div>
                    <Footer/>
                </div>
                <Toaster/>
                <HelpOverlay/>
                </PageTitleProvider>
                </AnnouncerProvider>
            </HelpProvider>
        </ThemeProvider>
    )
}

function App() {
    const services = useMemo(() => createRealServices(), [])

    return (
        <ServiceProvider services={services}>
            <ServerStatusProvider>
                <AuthProvider>
                    <AppShell/>
                </AuthProvider>
                <ServerUnavailableOverlay/>
            </ServerStatusProvider>
        </ServiceProvider>
    )
}


function PlaceholderPage({title}: { title: string }) {
    const i18n = useI18n()
    /* Feed the central PageH1 so SR users hear the placeholder title on
       navigation. The visible heading stays styled as a page heading but is
       structurally an H2 to avoid duplicating the H1. Issue 794. */
    usePageTitle(title)
    return (
        <SectionContainer>
            <h2 className="text-3xl font-bold tracking-tight" data-testid="placeholder-page-title">{title}</h2>
            <p className="text-muted-foreground mt-2">{i18n.common.comingSoon}</p>
        </SectionContainer>
    )
}

export default App
